// The online services the company actually pays for, as one-tap starting points
// for "Add service". Picking one fills the name, vendor, kind of payment,
// billing cycle, category and the vendor's billing page — the person is left
// with the amount, the date and the card.
//
// This is a typing shortcut, not a managed list: every field stays editable,
// nothing reports on `key`, and a service missing from here is simply added
// with "Something else". The chosen key is stored as Subscription.serviceKey
// and only decides the coloured logo tile.
//
// Prices are deliberately NOT pre-filled — they change, and the figure that
// matters is the one on the card statement.
//
// `match` holds lowercase, alphanumeric-only fragments used to recognise
// records created before the catalogue existed ("open ai", "google ads"), so
// they get their logo without any data being rewritten. Order matters: more
// specific entries (Shopify app, Claude API) come before the general ones.

export const SERVICE_GROUPS = [
    'Advertising',
    'AI tools',
    'Hosting & domains',
    'App stores',
    'APIs & data',
    'E-commerce',
    'Design & media',
    'Workspace'
];

export const SERVICE_CATALOG = [
    // ---- Advertising: billed to the card whenever spend hits a threshold ----
    {
        key: 'meta-ads', name: 'Meta Ads', vendor: 'Meta Platforms', group: 'Advertising',
        paymentType: 'usage', category: 'Marketing', color: '#0866FF', initials: 'M',
        billingUrl: 'https://business.facebook.com/billing_hub/payment_activity',
        hint: 'Facebook & Instagram ads — charged each time spend reaches the billing threshold',
        match: ['metaads', 'facebookads', 'instagramads', 'facebook', 'metabusiness']
    },
    {
        key: 'google-ads', name: 'Google Ads', vendor: 'Google', group: 'Advertising',
        paymentType: 'usage', category: 'Marketing', color: '#1A73E8', initials: 'GA',
        billingUrl: 'https://ads.google.com/aw/billing/summary',
        hint: 'Charged automatically as ads spend accrues',
        match: ['googleads', 'adwords']
    },

    // ---- AI tools ----
    {
        key: 'claude-api', name: 'Claude API', vendor: 'Anthropic', group: 'AI tools',
        paymentType: 'usage', category: 'AI Tools', color: '#C96442', initials: 'CA',
        billingUrl: 'https://console.anthropic.com/settings/billing',
        hint: 'Prepaid API credits — record each top-up',
        match: ['claudeapi', 'anthropicapi', 'anthropicconsole']
    },
    {
        key: 'claude', name: 'Claude', vendor: 'Anthropic', group: 'AI tools',
        paymentType: 'subscription', billingCycle: 'monthly', category: 'AI Tools', color: '#D97757', initials: 'C',
        billingUrl: 'https://claude.ai/settings/billing',
        hint: 'Pro / Max / Team plan',
        match: ['claude', 'anthropic']
    },
    {
        key: 'openai-api', name: 'OpenAI API', vendor: 'OpenAI', group: 'AI tools',
        paymentType: 'usage', category: 'AI Tools', color: '#10A37F', initials: 'AI',
        billingUrl: 'https://platform.openai.com/settings/organization/billing/overview',
        hint: 'Prepaid credits / auto-recharge — record each charge',
        match: ['openaiapi', 'openai']
    },
    {
        key: 'chatgpt', name: 'ChatGPT', vendor: 'OpenAI', group: 'AI tools',
        paymentType: 'subscription', billingCycle: 'monthly', category: 'AI Tools', color: '#0D0D0D', initials: 'GP',
        billingUrl: 'https://chatgpt.com/',
        hint: 'Plus / Team plan',
        match: ['chatgpt']
    },
    {
        key: 'gemini', name: 'Google AI Pro (Gemini)', vendor: 'Google', group: 'AI tools',
        paymentType: 'subscription', billingCycle: 'monthly', category: 'AI Tools', color: '#4285F4', initials: 'Ge',
        billingUrl: 'https://one.google.com/',
        hint: 'Gemini app plan, billed through Google One',
        match: ['googleaipro', 'geminiadvanced', 'geminipro', 'googleone']
    },
    {
        key: 'google-cloud-ai', name: 'Gemini API / Veo', vendor: 'Google Cloud', group: 'AI tools',
        paymentType: 'usage', category: 'AI Tools', color: '#34A853', initials: 'V',
        billingUrl: 'https://console.cloud.google.com/billing',
        hint: 'Gemini API, Veo video, Vertex AI — billed monthly by usage',
        match: ['veo', 'vertex', 'geminiapi', 'googlecloud', 'aistudio', 'gemini']
    },
    {
        key: 'removebg', name: 'remove.bg', vendor: 'Canva (Kaleido)', group: 'AI tools',
        paymentType: 'subscription', billingCycle: 'monthly', category: 'AI Tools', color: '#2D74FF', initials: 'rb',
        billingUrl: 'https://www.remove.bg/dashboard',
        hint: 'Image background removal — subscription or credits',
        match: ['removebg', 'backgroundremov', 'bgremov', 'unscreen']
    },
    {
        key: 'fotor', name: 'Fotor', vendor: 'Fotor', group: 'Design & media',
        paymentType: 'subscription', billingCycle: 'monthly', category: 'AI Tools', color: '#1B6EF3', initials: 'Fo',
        billingUrl: 'https://www.fotor.com/',
        hint: 'Photo / video editing and AI background removal',
        match: ['fotor']
    },
    {
        key: 'canva', name: 'Canva Pro', vendor: 'Canva', group: 'Design & media',
        paymentType: 'subscription', billingCycle: 'yearly', category: 'Software', color: '#00C4CC', initials: 'Ca',
        billingUrl: 'https://www.canva.com/settings/billing-and-plans',
        match: ['canva']
    },

    // ---- Hosting & domains ----
    {
        key: 'hostinger', name: 'Hostinger', vendor: 'Hostinger', group: 'Hosting & domains',
        paymentType: 'subscription', billingCycle: 'monthly', category: 'Cloud', color: '#673DE6', initials: 'H',
        billingUrl: 'https://hpanel.hostinger.com/billing/subscriptions',
        hint: 'VPS / web hosting / email',
        match: ['hostinger', 'kvm']
    },
    {
        key: 'domain', name: 'Domain renewal', vendor: '', group: 'Hosting & domains',
        paymentType: 'subscription', billingCycle: 'yearly', category: 'Cloud', color: '#0F766E', initials: 'D',
        hint: 'Yearly domain name renewal',
        match: ['domain']
    },

    // ---- App stores: developer accounts ----
    {
        key: 'google-play', name: 'Google Play Console', vendor: 'Google', group: 'App stores',
        paymentType: 'onetime', category: 'Developer & APIs', color: '#01875F', initials: '▶',
        billingUrl: 'https://play.google.com/console',
        hint: 'One-time developer registration fee',
        match: ['googleplay', 'playstore', 'playconsole']
    },
    {
        key: 'apple-developer', name: 'Apple Developer Program', vendor: 'Apple', group: 'App stores',
        paymentType: 'subscription', billingCycle: 'yearly', category: 'Developer & APIs', color: '#1D1D1F', initials: 'A',
        billingUrl: 'https://developer.apple.com/account',
        hint: 'App Store membership — renews every year',
        match: ['appledeveloper', 'appstore', 'appleprogram']
    },
    {
        key: 'chrome-webstore', name: 'Chrome Web Store', vendor: 'Google', group: 'App stores',
        paymentType: 'onetime', category: 'Developer & APIs', color: '#EA4335', initials: 'CW',
        billingUrl: 'https://chrome.google.com/webstore/devconsole',
        hint: 'One-time developer registration fee',
        match: ['chromewebstore', 'webstore', 'chromeextension']
    },

    // ---- APIs & data ----
    {
        key: 'serpapi', name: 'SerpApi', vendor: 'SerpApi', group: 'APIs & data',
        paymentType: 'subscription', billingCycle: 'monthly', category: 'Developer & APIs', color: '#4F46E5', initials: 'Sp',
        billingUrl: 'https://serpapi.com/dashboard',
        hint: 'Google search results API — monthly plan',
        match: ['serpapi', 'serp']
    },
    {
        key: 'brave-search', name: 'Brave Search API', vendor: 'Brave Software', group: 'APIs & data',
        paymentType: 'usage', category: 'Developer & APIs', color: '#FB542B', initials: 'B',
        billingUrl: 'https://api-dashboard.search.brave.com/',
        hint: 'Billed monthly by number of queries',
        match: ['brave']
    },

    // ---- E-commerce ----
    {
        key: 'shopify-app', name: 'Shopify app', vendor: 'Shopify', group: 'E-commerce',
        paymentType: 'subscription', billingCycle: 'monthly', category: 'Software', color: '#5E8E3E', initials: 'Sa',
        billingUrl: 'https://admin.shopify.com/settings/billing',
        hint: 'A paid app — appears on the Shopify bill',
        match: ['shopifyapp', 'shopifyproduct', 'shopifyplugin']
    },
    {
        key: 'shopify', name: 'Shopify plan', vendor: 'Shopify', group: 'E-commerce',
        paymentType: 'subscription', billingCycle: 'monthly', category: 'Software', color: '#95BF47', initials: 'S',
        billingUrl: 'https://admin.shopify.com/settings/billing',
        hint: 'The store subscription itself',
        match: ['shopify']
    },

    // ---- Workspace ----
    {
        key: 'google-workspace', name: 'Google Workspace', vendor: 'Google', group: 'Workspace',
        paymentType: 'subscription', billingCycle: 'monthly', category: 'Software', color: '#188038', initials: 'W',
        billingUrl: 'https://admin.google.com/ac/billing',
        match: ['googleworkspace', 'gsuite']
    },
    {
        key: 'microsoft-365', name: 'Microsoft 365', vendor: 'Microsoft', group: 'Workspace',
        paymentType: 'subscription', billingCycle: 'monthly', category: 'Software', color: '#D83B01', initials: 'MS',
        billingUrl: 'https://admin.microsoft.com/#/subscriptions',
        match: ['microsoft365', 'office365', 'm365']
    }
];

const normalise = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export const findService = (key) => SERVICE_CATALOG.find(s => s.key === key) || null;

// The catalogue entry for a record: its stored key, else a best guess from its
// name and vendor (for records made before the catalogue existed).
export const serviceFor = (record = {}) => {
    if (record.serviceKey) {
        const stored = findService(record.serviceKey);
        if (stored) return stored;
    }
    const haystack = normalise(`${record.name || record.subscriptionName || ''} ${record.vendor || ''}`);
    if (!haystack) return null;
    return SERVICE_CATALOG.find(s => s.match.some(m => haystack.includes(m))) || null;
};

// Search box filter for the "Which service?" step
export const searchServices = (term) => {
    const q = normalise(term);
    if (!q) return SERVICE_CATALOG;
    return SERVICE_CATALOG.filter(s =>
        normalise(`${s.name} ${s.vendor} ${s.group} ${s.hint || ''}`).includes(q) ||
        s.match.some(m => m.includes(q) || q.includes(m))
    );
};
